import * as calcApi from '@/api/calc';
import { jobStateEnum } from '@/api/job';
import { BottomTabsKey, useTabKeyStore } from '@/utils/bottomTabsKeyStore';
import { useJobStore } from '@/utils/jobService';
import { useSplitterStore } from '@/utils/splitterStore';
import { App } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** The Gaussian job types the platform's calculation menu offers. */
export type CalculationType = 'TSopt' | 'optimize' | 'ir' | 'uvvis' | 'raman' | 'freq' | 'optAndFreq';

export interface SubmitCalculationInput {
    type: CalculationType;
    charge: number;
    multiplicity: number;
}

/**
 * Submits a calculation for the file the 3D pane is showing.
 *
 * Identical behaviour to the JSmol pane's `start()`: tag the job with
 * `<user>_<unix>`, register it with the job store, then open the output tab so
 * the user immediately sees the run they just started.
 */
export function useCalculationJob(path?: string) {
    const { message } = App.useApp();
    const { t } = useTranslation('common');
    const addJob = useJobStore((state) => state.addJob);
    const setBottomTabsActiveKey = useTabKeyStore((state) => state.setBottomTabsActiveKey);
    const setOutputTabsActiveKey = useTabKeyStore((state) => state.setOutputTabsActiveKey);
    const setVerticalSizes = useSplitterStore((state) => state.setVerticalSizes);
    const [submitting, setSubmitting] = useState(false);

    const submit = useCallback(
        async ({ type, charge, multiplicity }: SubmitCalculationInput): Promise<boolean> => {
            if (!path) {
                message.warning(t('aitomol.no_file'));
                return false;
            }
            setSubmitting(true);
            try {
                const user = localStorage.getItem('userName');
                const tag = `${user}_${dayjs().unix().toString()}`;
                const response = await calcApi.postCalculation({ path, type, comment: tag, charge, multiplicity });
                if (!response?.ok) {
                    message.error(response?.message ?? t('aitomol.job_failed'));
                    return false;
                }
                if (response.data) {
                    addJob({
                        job_name: response.data.job_name,
                        path: response.data.path,
                        state: jobStateEnum.PENDING,
                        created_at: response.data.time || Math.floor(dayjs().unix() / 1000),
                        tag
                    });
                    setVerticalSizes(['80%', '20%']);
                    setBottomTabsActiveKey(BottomTabsKey.OUTPUT);
                    setOutputTabsActiveKey(tag);
                }
                message.success(t('aitomol.job_submitted'));
                return true;
            } catch (error) {
                console.error('[aitomol] job submission failed', error);
                message.error(t('aitomol.job_failed'));
                return false;
            } finally {
                setSubmitting(false);
            }
        },
        [path, addJob, message, setBottomTabsActiveKey, setOutputTabsActiveKey, setVerticalSizes, t]
    );

    return { submit, submitting };
}